// Bridge a publisher's intake submission into the PublisherConfig shape that
// PublisherForm's `initial` prop takes.
//
// The intake deliberately collects none of the internal-only fields, and
// PublisherConfig requires several of them. For each of those this supplies
// the value that EXACTLY EQUALS PublisherForm's own create-mode default, so
// nothing renders as pre-filled that the publisher didn't actually provide —
// the admin still has to make those choices themselves.

import type { PublisherConfig } from '../../../types/selfService';
import type { IntakeWirePayload, PublisherIntakeSubmission } from '../../../types/publisherIntake';
import { defaultLength, type TimelineClip } from './clipTimeline';

/** PublisherForm's create-mode default for revSharePercent (rendered as "25"). */
const DEFAULT_REV_SHARE = 0.25;

function num(value: unknown, fallback: number): number {
  // Explicit emptiness first: form v2 payloads store minBudget/minCampaignDays
  // as literal null, and Number(null) is 0 — a "missing" that silently became
  // a zero-dollar minimum instead of the create-mode default.
  if (value === null || value === undefined || value === '') return fallback;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function publisherIntakeToConfig(
  submission: PublisherIntakeSubmission,
): PublisherConfig {
  const p = (submission.payload ?? {}) as Partial<IntakeWirePayload>;

  return {
    slug: p.slug ?? submission.slug ?? '',
    // CTV-745: the intake's one name question asks what the station or
    // channel is called publicly, so it seeds the consumer-facing display
    // name. The internal name (the Publica-matching identifier) is the
    // team's to fill at finalize and is deliberately never pre-filled.
    // Legacy pre-v2 payloads that carried an explicit displayName keep it.
    name: '',
    displayName: p.displayName || p.name || submission.stationName || null,

    // Publisher-supplied branding.
    logoUrl: p.logoUrl ?? '',
    logoInvert: p.logoInvert ?? false,
    logoScale: num(p.logoScale, 1),
    primaryColor: p.primaryColor ?? '',
    secondaryColor: p.secondaryColor ?? '',
    accentColor: p.accentColor ?? '',

    markets: (p.markets ?? []).map((m) => ({
      dmaCode: m.dmaCode,
      dmaName: m.dmaName,
      // Call signs are an internal mapping the admin fills in at finalize.
      channels: [],
      coveredStates: m.coveredStates ?? [],
    })),

    minBudget: num(p.minBudget, 250),
    minCampaignDays: num(p.minCampaignDays, 7),

    // CTV-855: the intake no longer asks about devices; the admin picks them
    // here, so nothing is pre-checked even if an older payload carries a key.
    devices: [],

    // PublisherForm reads initial.products[0] to seed the CPM input. The empty
    // subChannels list leaves the Publica checkbox grid untouched, so it
    // reloads live once the admin picks an organization.
    products: [{
      id: 'intake',
      name: 'Run of Network',
      description: '',
      cpm: num(p.ronCpm, 0),
      subChannels: [],
    }],

    // The owning org was chosen by the super admin when they minted the link,
    // and the server pins it from the intake row regardless of what the body
    // says. Carrying it through means the admin isn't asked to re-pick
    // something already decided (and can't pick something that would be
    // silently overridden).
    organizationId: submission.organizationId,

    // Publisher-supplied contact, pre-filled for the admin to confirm or fix.
    // Both spellings: nested from submit, flat from a mid-revision autosave.
    adopsEmail: p.adOpsContact?.email ?? p.adOpsEmail ?? '',
    adopsPhone: p.adOpsContact?.phone ?? p.adOpsPhone ?? '',
    // The publisher's toggle pre-checks the form's commission checkbox even
    // when no station-wide % was given (NULL in the DB is what expresses
    // "varies per person"; the checkbox is how the admin sees the intent).
    defaultCommissionPercent:
      p.teamCommission?.defaultPercent
      ?? (p.showTeamCommission
        && p.teamCommissionPercent?.trim()
        && Number.isFinite(Number(p.teamCommissionPercent))
        ? Number(p.teamCommissionPercent)
        : null),
    teamCommissionEnabled:
      p.teamCommission?.enabled === true || p.showTeamCommission === true,

    // ---- internal-only: create-mode defaults, NOT publisher input ----
    revSharePercent: DEFAULT_REV_SHARE,
    coverage: 'regional',
    supportEmail: '',
    termsUrl: '#',
    fontFamily: '',
    fontUrl: undefined,
    backgroundVideoUrl: null,
    // Type-required; PublisherForm never reads it.
    serviceFeeRates: { percent: 0, fixedCents: 0 },
  };
}

/**
 * The intake's clip specs as editor clips, so the admin's create-mode submit
 * renders the background video from the footage the publisher sent.
 *
 * previewUrl comes from the signed URLs on the template response: the clips
 * bucket is private, and the object URLs the uploader normally uses only exist
 * on the machine that did the uploading. Without them the timeline renders but
 * nothing plays.
 *
 * The publisher's uploader doesn't trim, so most submissions carry no
 * in/out points. Lengths are left at 0 here and filled in by probing the
 * signed URL (see probeIntakeClipDurations), because a zero-length clip can't
 * be played or rendered.
 */
export function intakeClipsToTimeline(
  submission: PublisherIntakeSubmission,
): TimelineClip[] {
  const clips = submission.payload?.backgroundClips ?? [];
  const urlByPath = new Map(
    (submission.backgroundClipPreviews ?? []).map((p) => [p.storagePath, p.url]),
  );
  return clips.map((c, i) => {
    const inSec = c.inSec ?? 0;
    const outSec = c.outSec ?? 0;
    return {
      id: `intake-clip-${i}`,
      storagePath: c.storagePath,
      name: clipLeafName(c.storagePath) || `Clip ${i + 1}`,
      previewUrl: urlByPath.get(c.storagePath) ?? '',
      durationSec: Math.max(outSec - inSec, 0),
      inSec,
      lengthSec: Math.max(outSec - inSec, 0),
      thumbnails: [],
    };
  });
}

/** `intake/<id>/<uuid>_<safeName>` → `<safeName>`. */
// Exported for the settings-page footage tray (CTV-745), which labels the
// publisher's intake clips the same way this template adapter does.
export function clipLeafName(storagePath: string): string {
  const leaf = storagePath.split('/').pop() ?? '';
  const underscore = leaf.indexOf('_');
  return underscore > 0 ? leaf.slice(underscore + 1) : leaf;
}

/**
 * Fill in durations the payload doesn't carry by reading each clip's metadata
 * from its signed URL, then lay the clips out end to end across the timeline.
 *
 * Resolves to null when nothing needed probing, so callers can skip a state
 * update entirely.
 */
export async function probeIntakeClipDurations(
  clips: TimelineClip[],
): Promise<TimelineClip[] | null> {
  const needsProbe = clips.some((c) => c.previewUrl && c.durationSec <= 0);
  if (!needsProbe) return null;

  const probed = await Promise.all(
    clips.map(async (clip) => {
      if (!clip.previewUrl || clip.durationSec > 0) return clip;
      const durationSec = await probeDuration(clip.previewUrl);
      return { ...clip, durationSec };
    }),
  );

  // Lay them out in order, each taking as much of the 24s timeline as it can.
  let used = 0;
  return probed.map((clip) => {
    if (clip.lengthSec > 0) {
      used += clip.lengthSec;
      return clip;
    }
    const lengthSec = defaultLength(clip.durationSec, used);
    used += lengthSec;
    return { ...clip, lengthSec };
  });
}

function probeDuration(url: string): Promise<number> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => resolve(v.duration || 0);
    v.onerror = () => resolve(0);
    v.src = url;
  });
}
